import { useEffect, useState, useCallback, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Trash2 } from 'lucide-react';
import {
  getEventClasses,
  createEventClass,
  updateEventClass,
  deleteEventClass,
} from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import ConfirmDialog from '../shared/ConfirmDialog';
import type { EventClass } from '../../types';
import { Alert, Badge, Button, DataTable, Field, Input, Label, NumberInput, Switch, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, formatCurrency } from '@racing/ui';

interface ClassesTabProps {
  eventId: number;
}

const emptyClass: Partial<EventClass> = {
  class_name: '',
  class_code: '',
  entry_fee: 0,
  late_fee: 0,
  buyback_fee: null,
  max_entries: null,
  display_order: 0,
  is_active: true,
};

const ClassesTab = ({ eventId }: ClassesTabProps) => {
  const [classes, setClasses] = useState<EventClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<Partial<EventClass>>(emptyClass);
  const [isEdit, setIsEdit] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const loadClasses = useCallback(async () => {
    try {
      setLoading(true);
      setClasses(await getEventClasses(eventId));
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const handleAdd = () => {
    setEditingClass(emptyClass);
    setIsEdit(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isEdit && editingClass.event_class_id) {
        await updateEventClass(eventId, editingClass.event_class_id, editingClass);
      } else {
        await createEventClass(eventId, editingClass);
      }
      setDialogOpen(false);
      loadClasses();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    if (pendingDeleteId === null) return;
    try {
      await deleteEventClass(eventId, pendingDeleteId);
      loadClasses();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
    setPendingDeleteId(null);
  };

  const columns = useMemo<ColumnDef<EventClass, any>[]>(
    () => [
      { accessorKey: 'class_name', header: 'Class Name', meta: { width: 160 } },
      { accessorKey: 'class_code', header: 'Code', meta: { width: 90 } },
      {
        accessorKey: 'entry_fee',
        header: 'Entry Fee',
        cell: ({ getValue }) => formatCurrency(getValue<number | null>()),
        meta: { width: 110, numeric: true },
      },
      {
        accessorKey: 'late_fee',
        header: 'Late Fee',
        cell: ({ getValue }) => formatCurrency(getValue<number | null>()),
        meta: { width: 110, numeric: true, priority: 'md' },
      },
      {
        accessorKey: 'buyback_fee',
        header: 'Buyback',
        cell: ({ getValue }) => formatCurrency(getValue<number | null>()),
        meta: { width: 110, numeric: true, priority: 'lg' },
      },
      { accessorKey: 'max_entries', header: 'Max', meta: { width: 80, numeric: true, priority: 'lg' } },
      { accessorKey: 'display_order', header: 'Order', meta: { width: 80, numeric: true, priority: 'lg' } },
      {
        accessorKey: 'is_active',
        header: 'Active',
        cell: ({ getValue }) =>
          getValue<boolean>() ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="neutral">Inactive</Badge>
          ),
        meta: { width: 100, priority: 'md' },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Delete ${row.original.class_name}`}
            className="text-danger hover:bg-danger-bg"
            onClick={(event) => {
              event.stopPropagation();
              setPendingDeleteId(row.original.event_class_id);
              setConfirmOpen(true);
            }}
          >
            <Trash2 />
          </Button>
        ),
        meta: { width: 60, incidental: true },
      },
    ],
    []
  );

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {error && (
        <Alert variant="danger" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="flex justify-end">
        <Button variant="outline" onClick={handleAdd}>
          <Plus /> Add Class
        </Button>
      </div>

      <DataTable
        data={classes}
        columns={columns}
        getRowId={(row) => String(row.event_class_id)}
        loading={loading}
        onRowClick={(row) => {
          setEditingClass({ ...row });
          setIsEdit(true);
          setDialogOpen(true);
        }}
        emptyMessage="No classes defined for this event"
        pageSizeOptions={[10, 25]}
        className="max-h-[26rem]"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit class' : 'Add class'}</DialogTitle>
            <DialogDescription>
              Entry, late and buyback fees are charged per entry in this class.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4 sm:grid-cols-2">
            <Field label="Class Name" required className="sm:col-span-2">
              {(p) => (
                <Input
                  value={editingClass.class_name || ''}
                  onChange={(e) => setEditingClass({ ...editingClass, class_name: e.target.value })}
                  {...p}
                />
              )}
            </Field>
            <Field label="Class Code">
              {(p) => (
                <Input
                  value={editingClass.class_code || ''}
                  onChange={(e) => setEditingClass({ ...editingClass, class_code: e.target.value })}
                  {...p}
                />
              )}
            </Field>
            <Field label="Display Order">
              {(p) => (
                <NumberInput
                  value={editingClass.display_order ?? 0}
                  onValueChange={(value) =>
                    setEditingClass({ ...editingClass, display_order: value ?? 0 })
                  }
                  integer
                  nullable={false}
                  {...p}
                />
              )}
            </Field>
            <Field label="Entry Fee" required>
              {(p) => (
                <NumberInput
                  value={editingClass.entry_fee ?? null}
                  onValueChange={(value) =>
                    setEditingClass({ ...editingClass, entry_fee: value ?? 0 })
                  }
                  nullable={false}
                  {...p}
                />
              )}
            </Field>
            <Field label="Late Fee">
              {(p) => (
                <NumberInput
                  value={editingClass.late_fee ?? null}
                  onValueChange={(value) =>
                    setEditingClass({ ...editingClass, late_fee: value ?? 0 })
                  }
                  nullable={false}
                  {...p}
                />
              )}
            </Field>
            <Field label="Buyback Fee" hint="Blank if not offered">
              {(p) => (
                <NumberInput
                  value={editingClass.buyback_fee ?? null}
                  onValueChange={(value) => setEditingClass({ ...editingClass, buyback_fee: value })}
                  {...p}
                />
              )}
            </Field>
            <Field label="Max Entries" hint="Blank means no cap">
              {(p) => (
                <NumberInput
                  value={editingClass.max_entries ?? null}
                  onValueChange={(value) => setEditingClass({ ...editingClass, max_entries: value })}
                  integer
                  {...p}
                />
              )}
            </Field>
            <div className="flex items-center gap-3 sm:col-span-2">
              <Switch
                id="class-active"
                checked={!!editingClass.is_active}
                onCheckedChange={(checked) =>
                  setEditingClass({ ...editingClass, is_active: checked })
                }
              />
              <Label htmlFor="class-active">Active</Label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete class?"
        message="This removes the class from the event. Registrations already entered in it are not deleted."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingDeleteId(null);
        }}
      />
    </div>
  );
};

export default ClassesTab;
